import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Fade, Typography, css, useTheme } from "@mui/material";
import styled from '@emotion/styled/macro';
import useCanvasRulersTool from "../store/canvas-rulers";
import { useCropperStore } from "../store/cropper/cropper-store";
import PassThroughDiv from "./PassThroughDiv";

const TickerTens = styled(Box)`
  font-size: 8px;
  position: absolute;
  bottom: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  &::before {
    content: "";
    position: absolute;
    background: #fff;
  }

  &[data-anchor=top] {

    span {
      position: absolute;
      bottom: 0;
      white-space: nowrap;
      line-height: 1;
      padding-left: 3px;
      padding-bottom: 2px;
    }

    &::before {
      bottom: 0;
      height: 60%;
      width: 1px;
    }
  }

  &[data-anchor=left] {

    span {
      position: absolute;
      top: 0;
      right: 0;
      white-space: nowrap;
      line-height: 1;
      padding-right: 1px;
      padding-top: 3px;
    }

    &::before {
      top: 0;
      right: 0;
      width: 60%;
      height: 1px;
    }
  }
`;

const Ticker = styled(Box, {})`
  background: ${props => props.theme.palette.grey[700]};
  position: absolute;
  display: flex;
  font-size: 0;
`;

const CanvasRuler = styled(Box, {
  shouldForwardProp: prop => !['anchor'].includes(prop),
})`
  --size: 1em;
  color: #fff;
  font-size: var(--size);
  background: ${props => props.theme.palette.grey[800]};
  height: ${props => props.anchor === 'top' ? 'var(--size)' : '100%'};
  width: ${props => props.anchor === 'top' ? '100%' : 'var(--size)'};
  line-height: var(--size);
  top: 0;
  left: 0;
  position: absolute;
  display: flex;
  justify-content: center;
  pointer-events: all;

  ${props => props.anchor === 'left' && css`
    ${TickerTens} {
      height: auto;
      width: 100%;
    }
  `}
`;

const HorizontalLine = styled(Box)`
  position: absolute;
  height: 1px;
  background-color: ${({theme}) => theme.palette.primary.main};
  width: 100%;
  pointer-events: none;
  z-index: 100;
`;

const VerticalLine = styled(Box)`
  position: absolute;
  width: 1px;
  background-color: ${({theme}) => theme.palette.primary.main};
  height: 100%;
  pointer-events: none;
  z-index: 100;
`;

const CanvasCoordinates = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const MouseInfo = styled(Box)`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 101;
  transition: opacity 0.2s ease-in-out;
`;

export default function CanvasRulers() {
  const cropperStore = useCropperStore();
  const theme = useTheme();
  const { shown, setShown } = useCanvasRulersTool();
  const [rulerTicks, setRulerTiks] = useState({
    top: [],
    left: []
  });

  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [mouseInfo, setMouseInfo] = useState({ xPx: 0, yPx: 0, xCm: 0, yCm: 0 });
  const [pixelPerCm, setPixelPerCm] = useState(0);

  const rulerTop = useRef();
  const rulerLeft = useRef();
  const horizontalLineRef = useRef(null);
  const verticalLineRef = useRef(null);
  const mouseInfoRef = useRef(null);

  const updateRuler = (anchor) => {
    if (!cropperStore?.element?.cropper) return;

    const cropperWidth = cropperStore.element.cropper.cropBoxData.width;
    const cropperHeight = cropperStore.element.cropper.cropBoxData.height;
    const wallWidth = cropperStore.preset.width;
    const wallHeight = cropperStore.preset.height;

    let rulerSize, wallSize, rulerRef;
    if (anchor === "top") {
      rulerSize = cropperWidth;
      wallSize = wallWidth;
      rulerRef = rulerTop.current;
    } else if (anchor === "left") {
      rulerSize = cropperHeight;
      wallSize = wallHeight;
      rulerRef = rulerLeft.current;
    } else {
      return; // Invalid anchor
    }
    
    if (!rulerRef) return;

    // -------- Ruler styles ---------
    if(anchor === "top"){
      rulerRef.firstElementChild.style.height = `100%`;
      rulerRef.firstElementChild.style.width = `${rulerSize}px`;
    }else{
      rulerRef.firstElementChild.style.height = `${rulerSize}px`;
      rulerRef.firstElementChild.style.width = `100%`;
      rulerRef.firstElementChild.style.top = `${cropperStore.element.cropper.cropBoxData.top}px`;
    }
    
    const pixelPerCmLocal = rulerSize / wallSize;

    if (anchor === "top") setPixelPerCm(pixelPerCmLocal);

    let ticksEveryCm = 10;
    if (window.innerWidth <= 1024) {
      ticksEveryCm = 20;
    } else if (window.innerWidth <= 768) {
      ticksEveryCm = 40;
    } else if (window.innerWidth <= 480) {
      ticksEveryCm = 60;
    }

    // Calcoliamo la posizione delle tacche
    const ticksArray = [{
      [anchor === "top" ? 'left' : 'top']: 0,
      label: 0,
      size: pixelPerCmLocal * ticksEveryCm
    }];

    for (let cm = ticksEveryCm; cm <= wallSize; cm += ticksEveryCm) {
      const tickPosition = cm * pixelPerCmLocal;
      ticksArray.push({
        [anchor === "top" ? 'left' : 'top']: tickPosition,
        label: cm,
        size: pixelPerCmLocal * ticksEveryCm,
      });
    }

    if (anchor === "left") {
      ticksArray.reverse();
      for (let i = 0; i < ticksArray.length; i++) {
        ticksArray[i].top =
          (wallHeight - ticksArray[i].label) * pixelPerCmLocal;
      }
    }

    setRulerTiks(state => {
      state[anchor] = ticksArray;
      return state;
    });
  };

  const updateRulers = () => {
    updateRuler("top");
    updateRuler("left");
  }

  function onMouseMove(event) {
    if (cropperStore?.element?.cropper && cropperStore.element.cropper.container && shown) {
      const { left: boxLeft, top: boxTop, width: boxWidth, height: boxHeight } = cropperStore.element.cropper.cropBoxData;
      const containerRect = cropperStore.element.cropper.container.getBoundingClientRect();
      const offsetX = containerRect.left;
      const offsetY = containerRect.top;
      
      let x = event.clientX - offsetX;
      let y = event.clientY - offsetY;

      // Keep x within the bounding box
      if (x < boxLeft) {
        x = boxLeft;
      } else if (x > boxLeft + boxWidth) {
        x = boxLeft + boxWidth;
      }

      // Keep y within the bounding box
      if (y < boxTop) {
        y = boxTop;
      } else if (y > boxTop + boxHeight) {
        y = boxTop + boxHeight;
      }
      
      const mouseX = x - boxLeft;
      const mouseY = y - boxTop;

      setMousePosition({ x, y });
      setMouseInfo({
        xPx: Math.round(mouseX),
        yPx: Math.round(mouseY),
        xCm: parseFloat((mouseX / pixelPerCm).toFixed(2)),
        yCm: parseFloat((cropperStore.preset.height - (mouseY / pixelPerCm)).toFixed(2)),
      });

    }else{
      setMousePosition({x: event.clientX, y: event.clientY})
    }
  }

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    return () => window.removeEventListener('mousemove', onMouseMove);
  }, [cropperStore.element, pixelPerCm, shown]);

  useEffect(() => {
    updateRulers();
  }, [cropperStore.preset, shown]);

  useLayoutEffect(() => {
    updateRulers();
    window.addEventListener('resize', updateRulers);
    return () => window.removeEventListener('resize', updateRulers);
  }, [ 
    cropperStore.element
  ]);

  useEffect(() => {
    if(horizontalLineRef.current){
      horizontalLineRef.current.style.top = `${mousePosition.y}px`;
    }
    if (verticalLineRef.current) {
      verticalLineRef.current.style.left = `${mousePosition.x}px`;
    }
    if (mouseInfoRef.current) {
      mouseInfoRef.current.style.left = `${mousePosition.x + 10}px`;
      mouseInfoRef.current.style.top = `${mousePosition.y + 10}px`;
    }
  }, [mousePosition]);
  
  return (
    <>
      <Fade in={shown && cropperStore.isCropperReady()}>
        <PassThroughDiv position="absolute" style={{ zIndex: theme.zIndex.canvasTools }}>
          <CanvasRuler ref={rulerTop} anchor="top">
            <Ticker>
              {rulerTicks.top.map(({left, label, size}, index) => (
                <TickerTens data-anchor="top" key={index} style={{ width: size, left }}>
                  <span>{label}</span>
                </TickerTens>
              ))}
            </Ticker>
          </CanvasRuler>
          <CanvasRuler ref={rulerLeft} anchor="left">
            <Ticker>
              {rulerTicks.left.map(({top, label, size}, index) => (
                <TickerTens data-anchor="left" key={index} style={{ height: size, top }}>
                  <span>{label}</span>
                </TickerTens>
              ))}
            </Ticker>
          </CanvasRuler>
          <CanvasCoordinates>
            <MouseInfo ref={mouseInfoRef}>
              <Typography>
                {`Cm X: ${mouseInfo.xCm}`} <br />
                {`Cm Y: ${mouseInfo.yCm}`}
              </Typography>
            </MouseInfo>
            <HorizontalLine ref={horizontalLineRef} />
            <VerticalLine ref={verticalLineRef} />
          </CanvasCoordinates>
        </PassThroughDiv>
      </Fade>
    </>
  );
}
